
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { BoListPanel } from 'bonomo/dist';
import CommunityNav from '@/components/community/widget/CommunityNav.vue';
import Datim from '@/components/shared/Datim.vue';
import CommunityChip from '@/components/shared/chips/CommunityChip.vue';
import UserListSummary from '@/components/user/UserListSummary.vue';
import UserIntegrationStatus from '@/components/user/UserIntegrationStatus.vue';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import { ICommunity } from '@/model/Community/communityInterfaces';
import { communityUserService, ICommunityUser } from '@/services/communityUserService';
import { BetterDataTableHeader } from '@/model/betterVuetifyInterfaces';
import { extractApiErrorMessage } from '@/helpers/errorsHelper';
import { downloadFileByUrl } from '@/helpers/downloadHelper';
import { PAGINATION_FIRST_PAGE, PAGINATION_DEFAULT_PER_PAGE, PAGINATION_PER_PAGE_OPTIONS } from '@/constants/pagination';

const communityModule = namespace('communityModule');

@Component({
  name: 'CommunityUsers',
  components: { BoListPanel, CommunityNav, Datim, CommunityChip, UserListSummary, UserIntegrationStatus, PaginationBar },
})
export default class CommunityUsers extends Vue {
  @Prop()
  protected communityId!: number;

  @communityModule.Getter('community')
  protected community!: ICommunity | null;

  @communityModule.Action('fetchCommunity')
  protected fetchCommunity!: (communityId: number) => Promise<any>;

  protected loading: boolean = true;
  protected downloadingCsv: boolean = false;
  protected users: ICommunityUser[] = [];

  protected currentPage: number = PAGINATION_FIRST_PAGE;
  protected perPage: number = PAGINATION_DEFAULT_PER_PAGE;
  protected lastPage: number = PAGINATION_FIRST_PAGE;
  protected total: number = 0;
  protected readonly perPageOptions: number[] = PAGINATION_PER_PAGE_OPTIONS;

  protected get userRows(): Array<ICommunityUser & { name: string }> {
    return this.users.map(user => ({
      ...user,
      name: [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
        || user.email || user.phone_number || '—',
    }));
  }

  protected get headers(): BetterDataTableHeader[] {
    const compactColumn = {
      width: '1%',
      class: 'text-no-wrap px-2',
      cellClass: 'text-no-wrap px-2',
    };

    return [
      { text: this.$t('common.id'), align: 'start', sortable: false, value: 'id', ...compactColumn },
      { text: this.$t('common.name'), align: 'start', sortable: false, value: 'name' },
      { text: this.$t('communities.detail.title'), align: 'end', sortable: false, value: 'community', ...compactColumn },
      { text: this.$t('users.userList.integration'), align: 'start', sortable: false, value: 'is_from_integration', ...compactColumn },
      { text: this.$t('users.userList.createdAt'), align: 'start', sortable: false, value: 'created_at', ...compactColumn },
      { text: this.$t('users.user.fields.last_logged_in.title'), align: 'start', sortable: false, value: 'last_logged_in', ...compactColumn },
    ];
  }

  async loadData() {
    this.loading = true;
    try {
      const [, page] = await Promise.all([
        this.fetchCommunity(this.communityId),
        communityUserService.fetchByCommunity(this.communityId, {
          page: this.currentPage,
          per_page: this.perPage,
        }),
      ]);
      this.users = page.data;
      this.currentPage = page.current_page;
      this.lastPage = page.last_page;
      this.total = page.total;
    } catch (e) {
      this.$uiBus.emit('alert', extractApiErrorMessage(e));
    } finally {
      this.loading = false;
    }
  }

  userRowClass(): string {
    return 'user-row';
  }

  toUserDetail(userId: number): any {
    return { name: 'user', params: { userId: `${userId}` } };
  }

  openUser(user: ICommunityUser) {
    return this.$router.push(this.toUserDetail(user.id));
  }

  protected onPageChange(page: number) {
    this.currentPage = page;
    this.loadData();
  }

  protected onPerPageChange(value: number) {
    this.perPage = value;
    this.currentPage = PAGINATION_FIRST_PAGE;
    this.loadData();
  }

  protected async downloadUsersCsv() {
    this.downloadingCsv = true;
    try {
      const users = await communityUserService.fetchAllByCommunity(this.communityId);
      const headers = ['id', 'email', 'name', 'last_name', 'register_date', 'status'];
      const rows = users.map((user: ICommunityUser) => [
        user.id,
        user.email || '',
        user.first_name || '',
        user.last_name || '',
        user.created_at || '',
        user.active ? 'active' : 'inactive',
      ]);
      const csv = [headers, ...rows]
        .map((line: Array<string | number>) => line.map(this.escapeCsvCell).join(','))
        .join('\n');
      const csvBlob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(csvBlob);
      downloadFileByUrl(url, `community-${this.communityId}-users.csv`);
      URL.revokeObjectURL(url);
    } catch (e) {
      this.$uiBus.emit('alert', extractApiErrorMessage(e));
    } finally {
      this.downloadingCsv = false;
    }
  }

  private escapeCsvCell(value: string | number): string {
    const safeValue = value === null || value === undefined ? '' : `${value}`;
    return `"${safeValue.replace(/"/g, '""')}"`;
  }
}
